import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from 'urql';
import { Switch } from '~/components/ui/nucleus/Switch';
import { ASSET_SHARING_HELP_TEXT } from '~/constants/assetSharing';
import { graphql } from '~/gql';

const AssetSharingDefaultsQuery = graphql(`
  query AssetSharingDefaults {
    assetSharingDefaults {
      shareAttributes
      shareJobHistory
      shareDeploymentHistory
    }
  }
`);

const UpdateAssetSharingDefaultsMutation = graphql(`
  mutation UpdateAssetSharingDefaults($input: AssetSharingDefaultsInput!) {
    updateAssetSharingDefaults(input: $input) {
      shareAttributes
      shareJobHistory
      shareDeploymentHistory
    }
  }
`);

type AssetSharingSettings = {
  shareAttributes: boolean;
  shareJobHistory: boolean;
  shareDeploymentHistory: boolean;
};

export function SharingPreferences() {
  const [{ data, fetching }, executeQuery] = useQuery({
    query: AssetSharingDefaultsQuery,
  });
  const [, updateDefaults] = useMutation(UpdateAssetSharingDefaultsMutation);

  // Local state for the form
  const [settings, setSettings] = useState<AssetSharingSettings>({
    shareAttributes: true,
    shareJobHistory: true,
    shareDeploymentHistory: false,
  });

  // Track if there are unsaved changes
  const [isDirty, setIsDirty] = useState(false);

  // Track saving state
  const [isSaving, setIsSaving] = useState(false);

  // Track save status for footer feedback
  const [saveStatus, setSaveStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string | null;
  }>({ type: null, message: null });

  // Ref to track timeout for auto-hiding success message
  const successTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize settings from server data
  useEffect(() => {
    if (data?.assetSharingDefaults) {
      setSettings({
        shareAttributes: data.assetSharingDefaults.shareAttributes,
        shareJobHistory: data.assetSharingDefaults.shareJobHistory,
        shareDeploymentHistory:
          data.assetSharingDefaults.shareDeploymentHistory,
      });
      setIsDirty(false);
    }
  }, [data]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  const handleChange = useCallback(
    (field: keyof AssetSharingSettings, value: boolean) => {
      setSettings((prev) => ({ ...prev, [field]: value }));
      setIsDirty(true);
      // Clear any previous save status when user makes a new change
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = null;
      }
      setSaveStatus({ type: null, message: null });
    },
    []
  );

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    setSaveStatus({ type: null, message: null });
    try {
      const result = await updateDefaults({ input: settings });

      if (result.error) {
        // Extract error message from GraphQL error
        const errorMessage =
          result.error.graphQLErrors?.[0]?.message ||
          result.error.message ||
          'Failed to save sharing preferences. Please try again.';

        // Clean up GraphQL error prefix if present
        const displayMessage = errorMessage.replace('[GraphQL] ', '');

        console.error('Failed to update asset sharing defaults:', result.error);
        setSaveStatus({ type: 'error', message: displayMessage });
      } else {
        setIsDirty(false);
        setSaveStatus({ type: 'success', message: 'Saved' });
        // Refetch the query to update the cache with the saved values
        executeQuery({ requestPolicy: 'network-only' });
        // Clear any existing timeout before setting a new one
        if (successTimeoutRef.current) {
          clearTimeout(successTimeoutRef.current);
        }
        // Auto-hide success message after 3 seconds
        successTimeoutRef.current = setTimeout(() => {
          setSaveStatus({ type: null, message: null });
          successTimeoutRef.current = null;
        }, 3000);
      }
    } catch (error) {
      console.error('Unexpected error saving sharing preferences:', error);
      setSaveStatus({
        type: 'error',
        message: 'An unexpected error occurred while saving. Please try again.',
      });
    } finally {
      setIsSaving(false);
    }
  }, [settings, updateDefaults, executeQuery]);

  if (fetching && !data) {
    return (
      <div className='flex flex-col gap-4 p-4'>
        <p className='text-secondary'>Loading...</p>
      </div>
    );
  }

  return (
    <div className='flex flex-1 flex-col gap-4 '>
      <div className='mb-2'>
        <p className='text-secondary'>
          {' '}
          Choose what information you want to share with external assignees.
        </p>
      </div>

      <div className='rounded-lg border border-outline-variant bg-white p-4'>
        <h3 className='mb-4 text-base font-medium text-primary'>
          Asset Information Sharing
        </h3>
        <p className='mb-4 text-sm text-secondary'>
          Choose what asset information is shared with external assignees when
          an asset is added to a job. These defaults can be changed for
          individual jobs.
        </p>

        <div className='flex flex-col gap-4'>
          <Switch
            name='shareAttributes'
            label='Attributes'
            help={ASSET_SHARING_HELP_TEXT.shareAttributes}
            checked={settings.shareAttributes}
            onChange={(checked: boolean) =>
              handleChange('shareAttributes', checked)
            }
          />

          <Switch
            name='shareJobHistory'
            label='Job History'
            help={ASSET_SHARING_HELP_TEXT.shareJobHistory}
            checked={settings.shareJobHistory}
            onChange={(checked: boolean) =>
              handleChange('shareJobHistory', checked)
            }
          />

          <Switch
            name='shareDeploymentHistory'
            label='Deployment History'
            help={ASSET_SHARING_HELP_TEXT.shareDeploymentHistory}
            checked={settings.shareDeploymentHistory}
            onChange={(checked: boolean) =>
              handleChange('shareDeploymentHistory', checked)
            }
          />
        </div>
      </div>

      {(isDirty || saveStatus.type) && (
        <div className='mt-auto flex items-center justify-between gap-3'>
          <div className='flex items-center gap-2'>
            {saveStatus.type === 'success' && (
              <>
                <FontAwesomeIcon
                  icon={faCheck as IconProp}
                  className='h-4 w-4 text-success'
                  aria-hidden='true'
                />
                <span className='text-sm text-success'>
                  {saveStatus.message}
                </span>
              </>
            )}
            {saveStatus.type === 'error' && (
              <span className='text-sm text-red-600' role='alert'>
                {saveStatus.message}
              </span>
            )}
          </div>
          <div className='flex items-center gap-3'>
            {isDirty && (
              <button
                type='button'
                className='rounded-md border border-outline-variant px-4 py-2 text-sm text-secondary hover:bg-surface-container-low'
                onClick={() => {
                  if (data?.assetSharingDefaults) {
                    setSettings({
                      shareAttributes:
                        data.assetSharingDefaults.shareAttributes,
                      shareJobHistory:
                        data.assetSharingDefaults.shareJobHistory,
                      shareDeploymentHistory:
                        data.assetSharingDefaults.shareDeploymentHistory,
                    });
                    setIsDirty(false);
                    setSaveStatus({ type: null, message: null });
                  }
                }}
              >
                Cancel
              </button>
            )}
            {isDirty && (
              <button
                type='button'
                className='hover:bg-brand/90 rounded-md bg-brand px-4 py-2 text-sm text-white shadow disabled:cursor-not-allowed disabled:opacity-50'
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
